import { ENROLLMENT_MANUAL_CODE_MAX_LENGTH } from '@occ/contracts';

/**
 * Lo que la página `/i` necesita saber sin preguntarle al backend: cómo se
 * escribe un código mientras se teclea y en qué máquina está quien lo teclea
 * (plan de enrolamiento por código, §7, pasos 1 y 3).
 *
 * Está aparte de la pantalla porque son funciones puras y son justo la parte con
 * más formas de equivocarse — las dos normalizaciones del §5.1 tienen un
 * historial de sorpresas y aquí se prueban sin montar React.
 */

/**
 * Base32 de Crockford. Es la **quinta** copia del alfabeto (backend,
 * `install.sh`, `install.ps1` y `EnrollmentPage`), y como la del formulario no
 * necesita coincidir byte a byte con las demás: aquí solo decide si lo que se
 * está escribiendo *podría* ser un código sorteado, y equivocarse solo cambia
 * dónde se pinta un guion. Quién existe de verdad lo decide la búsqueda por hash
 * del servidor.
 */
const CROCKFORD = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';

/** Los seis caracteres del aleatorio y su agrupación de 3 en 3 (§5). */
const CODE_CHARS = 6;
const CODE_GROUP = 3;

/** Lo que las dos normalizaciones del §5.1 tiran antes de buscar. */
const SEPARATORS = /[\s-]+/g;
/** El mismo juego sin `g`: `.test()` con bandera global lleva cursor y miente. */
const HAS_SEPARATOR = /[\s-]/;

/** Lo que se puede teclear: el `A-Z0-9` del espacio manual más los separadores. */
const NOT_A_CODE = /[^A-Z0-9\s-]+/g;

/** El texto sin separadores y en mayúsculas, que es lo que se manda al servidor. */
export const stripInstallCode = (raw: string): string => raw.replace(SEPARATORS, '').toUpperCase();

/** `4K7M9Q` → `4K7-M9Q`. */
const group = (secret: string): string => {
  const grupos: string[] = [];
  for (let inicio = 0; inicio < secret.length; inicio += CODE_GROUP) {
    grupos.push(secret.slice(inicio, inicio + CODE_GROUP));
  }
  return grupos.join('-');
};

/**
 * Si el texto es exactamente lo que el sorteo sabe producir. Es la misma
 * frontera que `codeNamespace()` usa en el backend para elegir el contador de
 * fallos, y por el mismo motivo: un código de seis del alfabeto es
 * indistinguible se haya sorteado o se haya elegido a mano.
 */
const looksRandom = (stripped: string): boolean =>
  stripped.length === CODE_CHARS && [...stripped].every((caracter) => CROCKFORD.includes(caracter));

/** Recorta a los caracteres que el contrato admite, sin contar separadores. */
const clamp = (texto: string): string => {
  let contados = 0;
  let salida = '';
  for (const caracter of texto) {
    if (!HAS_SEPARATOR.test(caracter)) {
      if (contados >= ENROLLMENT_MANUAL_CODE_MAX_LENGTH) break;
      contados += 1;
    }
    salida += caracter;
  }
  return salida;
};

/**
 * Cómo se ve el código mientras se escribe (§7, paso 1).
 *
 * Normaliza siempre —mayúsculas y fuera todo lo que no puede formar parte de un
 * código— y **agrupa de 3 en 3 solo los aleatorios**: partir `FRUTAL-7K2M` por la
 * mitad no ayuda a nadie a dictarlo.
 *
 * El guion se pone en un único momento: cuando lo escrito son seis caracteres del
 * alfabeto **y no se tecleó ningún separador**. Las dos condiciones están para
 * evitar el mismo daño desde dos lados. La primera, porque antes del sexto
 * carácter no se sabe si esto va a ser un aleatorio o una etiqueta larga, y un
 * guion que aparece al cuarto carácter para desaparecer al séptimo es un campo
 * que se mueve solo mientras se escribe. La segunda, porque quien teclea
 * `CAJA3-7K2M` ya dijo dónde quiere el guion, y moverlo al tercer carácter le
 * cambiaría delante de los ojos algo que él escribió bien.
 *
 * Nada de esto cambia lo que el servidor busca: el hash va sobre el texto sin
 * separadores, así que el guion es presentación en los dos espacios.
 */
export function formatInstallCode(raw: string): string {
  const limpio = clamp(raw.toUpperCase().replace(NOT_A_CODE, ''));
  const pelado = limpio.replace(SEPARATORS, '');

  if (!HAS_SEPARATOR.test(limpio) && looksRandom(pelado)) return group(pelado);
  return limpio;
}

/** Los tres caminos que la página sabe entregar. Uno por comando del §6. */
export type InstallTarget = 'windows' | 'windows7' | 'linux';

/**
 * Lo que se puede deducir del navegador, que es más de lo que se puede entregar:
 * `macos` existe como resultado porque el agente **no** corre ahí todavía
 * (`install.sh` muere con "en macOS el agente aún no está soportado"), y decirlo
 * en la página es más útil que ofrecer un comando que va a fallar.
 */
export type DetectedSystem = InstallTarget | 'macos' | 'unknown';

/**
 * De qué sistema es la caja, mirando el `User-Agent` (§7, paso 3).
 *
 * Acierta casi siempre, y "casi siempre" es justo por qué la página pone el
 * selector manual al lado: el comando equivocado no falla de forma visible, se
 * pega en la consola que no era y no pasa nada hasta que alguien se pregunta por
 * qué la caja no aparece en el panel.
 *
 * `Windows NT 6.1` y anteriores caen en `windows7` porque es donde está la
 * frontera de verdad: esas cajas traen PowerShell 2.0 y TLS 1.0, y por eso
 * existen `win7-bootstrap/` e `install-win7.ps1`. De `NT 6.2` en adelante el
 * comando normal sirve.
 */
export function detectSystem(userAgent: string): DetectedSystem {
  if (/Windows NT (5\.|6\.0|6\.1)/.test(userAgent)) return 'windows7';
  if (/Windows/i.test(userAgent)) return 'windows';
  // Antes que Linux: el `User-Agent` de Android lleva "Linux" dentro y no es una
  // caja donde se pueda instalar nada. Sin sistema reconocido la página no
  // adivina — enseña el selector con Windows por delante y lo dice.
  if (/Android/i.test(userAgent)) return 'unknown';
  if (/Macintosh|Mac OS X/i.test(userAgent)) return 'macos';
  if (/Linux|X11|CrOS/i.test(userAgent)) return 'linux';
  return 'unknown';
}

/** Con qué comando se abre la página cuando la detección no llega a nada. */
export const targetFor = (detected: DetectedSystem): InstallTarget =>
  detected === 'macos' || detected === 'unknown' ? 'windows' : detected;
